import React, { useCallback } from 'react';
import classNames from 'classnames';
import { NavLink } from 'react-router-dom';
import { useFeature } from '@growthbook/growthbook-react';
import { Icon } from '@modulor/react';
import Local from '../../Local/Local';
import Skeleton from '../../Skeleton/Skeleton';
import NavigationBlock from '../NavigationBlock/NavigationBlock';
import { ReactComponent as ThemesIcon } from '../../../assets/icons/image_alt.svg'; // no modulor icon
import { LinkWithAnalytics } from '../../Link/Link';
import withAnalytics from '../../../hocs/withAnalytics';
import { useThemes } from './hooks';
import { useIsDesktopLayout } from '../../../hooks/useIsDesktopLayout';
import { useAnalyticsScreenName } from '../../../hooks/useAnalyticsScreenName';
import { getThemeDetailRoute, getThemesRoute } from '../../../utils/routes';
import { getDataTestAttrs } from '../../../utils/testing-locators';
import { EVENT_ACTION } from '../../../constants/ga';
import { NAMESPACES } from '../../../constants/namespaces';
import { FEATURES } from '../../../constants/featureToggling';
import {
  NAVIGATION_THEME_LINK,
  NAVIGATION_THEMES,
  NAVIGATION_THEMES_LINK,
  NAVIGATION_THEMES_LINK_ARROW,
  NAVIGATION_THEMES_LINK_ICON,
  NAVIGATION_THEMES_TOGGLE,
} from '../../../constants/dataTestIds';
import {
  ANALYTICS_SCHEMA_URLS,
  ANALYTICS_SCREEN_AREAS,
  ANALYTICS_SCREEN_AREA_DETAILS,
} from '../../../constants/analytics';
import styles from '../NavigationBlock.module.css';

export const NavLinkWithAnalytics = withAnalytics(NavLink);

const NavigationBlockThemes = ({ className }) => {
  const isDesktop = useIsDesktopLayout();
  const screenName = useAnalyticsScreenName();

  const isEnabledThemes = useFeature(FEATURES.USE_THEMES).on;

  const { query, collection, isExpanded, toggleExpand } = useThemes();

  const renderItem = useCallback(
    ({ id, isExpandButton, translationKey }, index) =>
      query.isLoading ? (
        <div className={styles.item}>
          <Skeleton className={styles.skeleton} />
        </div>
      ) : isExpandButton ? (
        <button
          onClick={toggleExpand}
          className={classNames(styles.item, styles.all)}
          {...getDataTestAttrs(NAVIGATION_THEMES_TOGGLE)}
        >
          {isExpanded ? <Local>Collapse</Local> : <Local>All</Local>}
        </button>
      ) : (
        <NavLinkWithAnalytics
          dataLayerEventData={{
            eventAction: EVENT_ACTION.NAV_THEME_ITEM,
            section_index: 2,
            position_index: index,
            theme_id: id,
          }}
          analyticsData={{
            schema: ANALYTICS_SCHEMA_URLS.MORE_GAME_CLICK,
            screen_name: screenName,
            screen_area: ANALYTICS_SCREEN_AREAS.SIDEBAR,
            screen_area_detail: ANALYTICS_SCREEN_AREA_DETAILS.THEME,
            event_detail: id,
            section_index: 2,
            position_index: index,
          }}
          to={getThemeDetailRoute(id)}
          className={(isActive) =>
            classNames(styles.item, isActive && styles.active)
          }
          {...getDataTestAttrs(NAVIGATION_THEME_LINK, id)}
        >
          <span>
            <Local namespace={NAMESPACES.THEME}>{translationKey}</Local>
          </span>
        </NavLinkWithAnalytics>
      ),
    [isExpanded, query.isLoading, toggleExpand, screenName]
  );

  return isEnabledThemes ? (
    <NavigationBlock
      collection={collection}
      title={
        <LinkWithAnalytics
          to={getThemesRoute()}
          dataLayerEventData={{
            eventAction: EVENT_ACTION.NAV_THEMES,
            section_index: 2,
          }}
          analyticsData={{
            schema: ANALYTICS_SCHEMA_URLS.ALL_CLASSIFICATION_CLICK,
            screen_name: screenName,
            screen_area: ANALYTICS_SCREEN_AREAS.SIDEBAR,
            screen_area_detail: ANALYTICS_SCREEN_AREA_DETAILS.ALL_THEME,
            section_index: 2,
          }}
          className={styles.title}
          {...getDataTestAttrs(NAVIGATION_THEMES_LINK)}
        >
          {!isDesktop && (
            <ThemesIcon
              className={styles.icon}
              {...getDataTestAttrs(NAVIGATION_THEMES_LINK_ICON)}
            />
          )}
          <Local namespace={NAMESPACES.PAGES}>Themes</Local>
          <span className={styles.goToAll}>
            <Local namespace={NAMESPACES.INITIAL}>All</Local>
          </span>
          <Icon
            name="chevron_right"
            className={styles.arrow}
            {...getDataTestAttrs(NAVIGATION_THEMES_LINK_ARROW)}
          />
        </LinkWithAnalytics>
      }
      renderItem={renderItem}
      className={className}
      {...getDataTestAttrs(NAVIGATION_THEMES)}
    />
  ) : null;
};

export default NavigationBlockThemes;
